import {useCallback, useEffect, useRef} from "react"
import debounce from 'lodash/debounce';

type StaleId = number;

export interface IStale {
    startAsyncCall: () => StaleId,
    isStale: (staleId: StaleId) => boolean,
}

// This is a small helper to abstract the logic for checking if your async results are stale.
// This is useful for times when the user might search again before the results from the first call come back.
// Example use case:
//     const stale = useStale();
//     function search(text: string) {
//         const staleId = stale.startAsyncCall();
//         api.doCall()
//             .then((results) => {
//                 // Ignore stale results
//                 if (stale.isStale(staleId)) return;

//                 // Handle results of api call
//             })
//     }
export function useStale(): IStale {
    const staleIdRef = useRef(1);

    return {
        // Returns a "staleId", which can be used to check if a call is stale
        startAsyncCall: () => {
            // Increment the current id to mark all previous calls as stale.
            staleIdRef.current++;

            // Return the new id.
            const staleId = staleIdRef.current;
            return staleId;
        },
        // Checks if the staleId from "startAsyncCall" is stale yet.
        isStale: (staleId: StaleId) => {
            // If another async call has been started, then "staleIdRef" will have been incremented.
            return staleId !== staleIdRef.current;
        },
    }
}